import React from 'react';
import type { PhoneTheme } from 'magical-trails';

export type GlyphId = 'messages' | 'map' | 'scanner' | 'codeEntry' | 'menu';

/**
 * The drawing on each app icon. One line-drawn set per family of looks, all
 * on a 24-unit grid with `currentColor` strokes, so the stylesheet decides
 * colour and the tile around it. Looks share a glyph where the object is the
 * same in their world (a key is a key); where it isn't (nobody in a spell
 * book owns a walkie-talkie) the look gets its own.
 */
type GlyphSet = Record<GlyphId, React.ReactNode>;

const modern: GlyphSet = {
  messages: (
    <>
      <path d="M4 6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5v7a2.5 2.5 0 0 1-2.5 2.5H11l-4.5 4v-4A2.5 2.5 0 0 1 4 13.5v-7Z" />
      <path d="M8.5 8.75h7M8.5 11.75h4.5" />
    </>
  ),
  map: (
    <>
      <path d="M9 4.5 3.5 6.5v13L9 17.5l6 2 5.5-2v-13L15 6.5l-6-2Z" />
      <path d="M9 4.5v13M15 6.5v13" />
    </>
  ),
  scanner: (
    <>
      <path d="M4 8V5.5A1.5 1.5 0 0 1 5.5 4H8M16 4h2.5A1.5 1.5 0 0 1 20 5.5V8M20 16v2.5a1.5 1.5 0 0 1-1.5 1.5H16M8 20H5.5A1.5 1.5 0 0 1 4 18.5V16" />
      <path d="M8 9h3v3H8zM13.5 9H16M13.5 12H16v3.5M8 15h3" />
    </>
  ),
  codeEntry: (
    <>
      <rect x="4" y="5" width="16" height="14" rx="2.5" />
      <path d="M8 10h.01M12 10h.01M16 10h.01M8 14h.01M12 14h.01M16 14h.01" />
    </>
  ),
  menu: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 3.5v2.25M12 18.25v2.25M3.5 12h2.25M18.25 12h2.25M6 6l1.6 1.6M16.4 16.4 18 18M18 6l-1.6 1.6M7.6 16.4 6 18" />
    </>
  ),
};

const blaster: GlyphSet = {
  ...modern,
  // A walkie-talkie: stubby aerial, speaker grille, push-to-talk.
  messages: (
    <>
      <path d="M9 7V3.5" />
      <rect x="6.5" y="7" width="11" height="13.5" rx="2.5" />
      <path d="M9.5 10.5h5M9.5 13h5" />
      <circle cx="12" cy="17" r="1.25" />
    </>
  ),
  // Target reticle over a pin.
  scanner: (
    <>
      <circle cx="12" cy="12" r="6.5" />
      <circle cx="12" cy="12" r="1.5" />
      <path d="M12 2.5v4M12 17.5v4M2.5 12h4M17.5 12h4" />
    </>
  ),
  map: (
    <>
      <path d="M12 21s6.5-5.7 6.5-10.5a6.5 6.5 0 1 0-13 0C5.5 15.3 12 21 12 21Z" />
      <path d="m9.5 10.5 1.8 1.8 3.4-3.6" />
    </>
  ),
};

const spyfi: GlyphSet = {
  ...modern,
  // A sealed dispatch.
  messages: (
    <>
      <rect x="3.5" y="6" width="17" height="12" rx="1.5" />
      <path d="m3.5 7.5 8.5 6 8.5-6" />
      <circle cx="12" cy="15.25" r="1.5" />
    </>
  ),
  // Lines of latitude and longitude.
  map: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M3.5 12h17M12 3.5c-3 2.3-3 14.7 0 17M12 3.5c3 2.3 3 14.7 0 17M5.3 7.5h13.4M5.3 16.5h13.4" />
    </>
  ),
  // A lens iris.
  scanner: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="m12 3.5 3.7 6.4M20.2 9.9h-7.4M17.6 18.4l3.7-6.4M12 20.5l-3.7-6.4M3.8 14.1h7.4M6.4 5.6 2.7 12" />
    </>
  ),
  // A rotary dial.
  codeEntry: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="2.25" />
      <path d="M12 6.25h.01M16.1 7.9h.01M17.75 12h.01M16.1 16.1h.01M12 17.75h.01M7.9 16.1h.01M6.25 12h.01" />
    </>
  ),
};

const steampunk: GlyphSet = {
  ...modern,
  // A telegram slip with its torn edge.
  messages: (
    <>
      <path d="M4.5 5h15v12l-2.5 2-2.5-2-2.5 2-2.5-2-2.5 2-2.5-2V5Z" />
      <path d="M8 9h8M8 12.5h5.5" />
    </>
  ),
  // A compass.
  map: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="m15.5 8.5-2 5-5 2 2-5 5-2Z" />
      <path d="M12 3.5V5M12 19v1.5M3.5 12H5M19 12h1.5" />
    </>
  ),
  // A lens in a cog.
  scanner: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2.75v2.5M12 18.75v2.5M2.75 12h2.5M18.75 12h2.5M5.45 5.45l1.8 1.8M16.75 16.75l1.8 1.8M18.55 5.45l-1.8 1.8M7.25 16.75l-1.8 1.8" />
      <circle cx="12" cy="12" r="7" />
    </>
  ),
  codeEntry: (
    <>
      <circle cx="8" cy="12" r="3.5" />
      <path d="M11.5 12H20M17 12v3M20 12v2.25" />
    </>
  ),
};

const spellbook: GlyphSet = {
  ...modern,
  // An owl, for the post.
  messages: (
    <>
      <path d="M6 9.5C6 6 8.5 4.5 12 4.5s6 1.5 6 5V14a6 6 0 0 1-12 0V9.5Z" />
      <path d="m6 5 2.5 1.75M18 5l-2.5 1.75" />
      <circle cx="9.5" cy="10.5" r="1.5" />
      <circle cx="14.5" cy="10.5" r="1.5" />
      <path d="m12 12.5-.9 1.5h1.8L12 12.5Z" />
    </>
  ),
  // A wandering star chart.
  map: (
    <>
      <path d="m12 3 1.9 5.6L19.5 9l-4.5 3.5 1.6 5.7L12 15l-4.6 3.2L9 12.5 4.5 9l5.6-.4L12 3Z" />
      <path d="M12 9.5v3.5" />
    </>
  ),
  // A revealing glass.
  scanner: (
    <>
      <circle cx="10.5" cy="10.5" r="6" />
      <path d="m15 15 5 5" />
      <path d="m10.5 7.75.75 2 2 .75-2 .75-.75 2-.75-2-2-.75 2-.75.75-2Z" />
    </>
  ),
  codeEntry: steampunk.codeEntry,
  // A ribboned bookmark.
  menu: (
    <>
      <path d="M7 4h10v16.5l-5-3.5-5 3.5V4Z" />
      <path d="M10 8.5h4" />
    </>
  ),
};

const SETS: Record<PhoneTheme, GlyphSet> = {
  modern,
  blaster,
  spyfi,
  steampunk,
  spellbook,
};

export interface Props {
  glyph: GlyphId;
  theme: PhoneTheme;
  className?: string;
}

const PhoneGlyph: React.FC<Props> = ({ glyph, theme, className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {SETS[theme][glyph]}
  </svg>
);

export default PhoneGlyph;
